import { Timestamp } from 'firebase/firestore';

/** Latest list hygiene scan (counts only; no PII). Used for dashboard prompts. */
export interface WorkspaceHygieneSnapshot {
  computedAt: Timestamp;
  noOpenDays: number;
  noClickDays: number;
  stalePendingDays: number;
  counts: {
    noRecentOpen: number;
    noRecentClick: number;
    stalePending: number;
    marketingPaused: number;
  };
  /** Dedupe key for "you have new hygiene issues" banner. */
  signature: string;
}

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  logo?: string;
  primaryColor: string;
  secondaryColor: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string; // userId
  settings: WorkspaceSettings;
  isActive: boolean;
  hygieneSnapshot?: WorkspaceHygieneSnapshot;
  hygieneBannerDismissedSignature?: string | null;
}

export interface WorkspaceSettings {
  timezone: string;
  currency: string;
  language: string;
  branding: WorkspaceBranding;
  notifications: WorkspaceNotifications;
  frequencyCap?: WorkspaceFrequencyCap; // global contact frequency cap across all automations
  integrations: WorkspaceIntegrations;
  address?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    region?: string;
    postalCode?: string;
    country: string;
    keepPrivate: boolean;
  };
}

export interface WorkspaceBranding {
  logo?: string;
  removeScubamailBranding?: boolean;
  primaryColor: string;
  secondaryColor: string;
  socialLinks: Array<{
    platform: string;
    url: string;
  }>;
  palette?: {
    accent: string;
    background: string;
    surface: string;
    text: string;
    link: string;
    success: string;
    warning: string;
    error: string;
  };
  favicon?: string;
  emailSignature?: string;
}

export interface WorkspaceNotifications {
  email: boolean;
  inApp: boolean;
  campaignUpdates: boolean;
  memberActivity: boolean;
  systemAlerts: boolean;
}

export interface WorkspaceFrequencyCap {
  enabled: boolean;
  maxPerDay: number;   // max automation emails to a single contact per calendar day
  maxPerHour: number;  // max automation emails to a single contact per rolling hour (0 = no hourly limit)
}

export interface WorkspaceIntegrations {
  stripe?: {
    customerId: string;
    subscriptionId?: string;
  };
  webhooks: Array<{
    id: string;
    url: string;
    events: string[];
    isActive: boolean;
  }>;
}

export interface WorkspaceMember {
  id: string;
  workspaceId: string;
  userId: string;
  email: string;
  displayName?: string;
  role: WorkspaceRole;
  invitedBy: string;
  joinedAt: Timestamp;
  lastActiveAt?: Timestamp;
  permissions: WorkspacePermissions;
  isActive: boolean;
}

export type WorkspaceRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface WorkspacePermissions {
  contacts: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    export: boolean;
  };
  campaigns: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    send: boolean;
  };
  templates: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
  };
  automations: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
  };
  analytics: {
    view: boolean;
    export: boolean;
  };
  settings: {
    view: boolean;
    edit: boolean;
  };
  members: {
    view: boolean;
    invite: boolean;
    remove: boolean;
    editRoles: boolean;
  };
  billing: {
    view: boolean;
    manage: boolean;
  };
}

export interface CreateWorkspaceData {
  name: string;
  description?: string;
  primaryColor: string;
  secondaryColor: string;
  timezone: string;
  currency: string;
  language: string;
}

export interface UpdateWorkspaceData {
  name?: string;
  description?: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  settings?: Partial<WorkspaceSettings>;
  hygieneSnapshot?: WorkspaceHygieneSnapshot;
  /** When equal to hygieneSnapshot.signature, dashboard hygiene banner stays hidden. */
  hygieneBannerDismissedSignature?: string | null;
}

export interface InviteMemberData {
  email: string;
  role: WorkspaceRole;
  message?: string;
}

export interface WorkspaceActivity {
  id: string;
  workspaceId: string;
  userId: string;
  userEmail: string;
  action: string;
  description: string;
  metadata?: Record<string, any>;
  timestamp: Timestamp;
  ipAddress?: string;
  userAgent?: string;
}

// Default permissions for each role
export const WORKSPACE_ROLE_PERMISSIONS: Record<WorkspaceRole, WorkspacePermissions> = {
  owner: {
    contacts: { view: true, create: true, edit: true, delete: true, export: true },
    campaigns: { view: true, create: true, edit: true, delete: true, send: true },
    templates: { view: true, create: true, edit: true, delete: true },
    automations: { view: true, create: true, edit: true, delete: true },
    analytics: { view: true, export: true },
    settings: { view: true, edit: true },
    members: { view: true, invite: true, remove: true, editRoles: true },
    billing: { view: true, manage: true },
  },
  admin: {
    contacts: { view: true, create: true, edit: true, delete: true, export: true },
    campaigns: { view: true, create: true, edit: true, delete: true, send: true },
    templates: { view: true, create: true, edit: true, delete: true },
    automations: { view: true, create: true, edit: true, delete: true },
    analytics: { view: true, export: true },
    settings: { view: true, edit: false },
    members: { view: true, invite: true, remove: true, editRoles: true },
    billing: { view: true, manage: false },
  },
  editor: {
    contacts: { view: true, create: true, edit: true, delete: false, export: true },
    campaigns: { view: true, create: true, edit: true, delete: false, send: true },
    templates: { view: true, create: true, edit: true, delete: false },
    automations: { view: true, create: true, edit: true, delete: false },
    analytics: { view: true, export: true },
    settings: { view: false, edit: false },
    members: { view: true, invite: false, remove: false, editRoles: false },
    billing: { view: false, manage: false },
  },
  viewer: {
    contacts: { view: true, create: false, edit: false, delete: false, export: false },
    campaigns: { view: true, create: false, edit: false, delete: false, send: false },
    templates: { view: true, create: false, edit: false, delete: false },
    automations: { view: true, create: false, edit: false, delete: false },
    analytics: { view: true, export: false },
    settings: { view: false, edit: false },
    members: { view: true, invite: false, remove: false, editRoles: false },
    billing: { view: false, manage: false },
  },
};
